import { useElementSize } from '@mantine/hooks';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Button,
  ButtonBase,
  Fade,
  FormControl,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useEffect, useRef, useState } from 'react';
import { isDeepEqual } from 'remeda';
import { bloxValues } from 'themes/constants';
import { emdash, int32Max } from 'utility/constants';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  getCloseButtonSx,
  getDropdownInputThreshold,
  getFooterContainerSx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  maxPopoverHeightVh,
  maxPopoverWidthPxRange,
  tooltipPopperProps,
} from './dropdownConsts';
import { PossibleEvent } from './SelectDropdown/SelectDropdownInput';
import { SelectRenderValueElem } from './SelectDropdown/SelectRenderValueElem';

type TwoStrArr = [string, string];
type ValueMode = 'normal' | 'currency'; // currency is for when values come in as cents eg 1000 = $10.00

type NumRangeDropdownInputProps = {
  inputLabel?: string;
  value?: TwoStrArr;
  updateValue: (event: PossibleEvent | any, value: TwoStrArr) => void;
  formControlSx?: SxProps<Theme>;
  popoverPaperSx?: SxProps<Theme>;
  formControlVariant?: 'standard' | 'outlined';
  renderValueTooltip?: boolean;
  renderValueDisplay?: 'label' | 'chips';
  popoverMatchesFormControlWidth?: boolean;
  applyChangesOnPopoverClose?: boolean;
  inputPlaceholderText?: string;
  disabled?: boolean;
  hidden?: boolean;
  startAdornment?: React.ReactNode;
  valueMode?: ValueMode;
  keepMounted?: boolean;
  infoText?: string;
  decimalPlaces?: number;
  prefix?: string;
  suffix?: string;
  fieldType?: HTMLInputElement['type']; // "number" | "tel", etc
  clamp?: boolean; // Enforce values to min/max?
  step?: number;
  min?: number;
  max?: number;
};

// Helper to format value for display string
function formatForDisplay(value: string, valueType: ValueMode, decimalPlaces: number) {
  const num = parseFloat(value);
  if (isNaN(num) || !value) {
    return '';
  }

  if (valueType === 'currency') {
    const result = (num / 100).toFixed(decimalPlaces);
    return result;
  }

  const result = num.toFixed(decimalPlaces);
  return result;
}

// Helper to format value for query params
function formatForQp(value: string, valueType: ValueMode) {
  const num = parseFloat(value);
  if (isNaN(num) || !value) {
    return '';
  }

  if (valueType === 'currency') {
    const result = (num * 100).toFixed(0); // eg cents for api
    return result;
  }

  return value;
}

// Get the raw text label without formatting
function getRangeLabel(
  range: TwoStrArr,
  valueType: ValueMode,
  decimalPlaces: number,
  prefix?: string,
  suffix?: string,
) {
  const [startValue, endValue] = range;
  const hasStart = startValue !== '';
  const hasEnd = endValue !== '';

  const startNum = hasStart ? formatForDisplay(startValue, valueType, decimalPlaces) : '';
  const endNum = hasEnd ? formatForDisplay(endValue, valueType, decimalPlaces) : '';

  const isEndBeforeStart =
    hasStart && hasEnd && parseFloat(startNum) > parseFloat(endNum);

  if (isEndBeforeStart) {
    return 'Min is greater than max value.';
  }

  if (startNum && endNum && startNum === endNum) {
    return `Exactly ${prefix}${startNum}${suffix}`;
  }

  if (startNum && endNum) {
    return ` ${prefix}${startNum}${suffix} to ${prefix}${endNum}${suffix}`;
  }

  if (startNum) {
    return `From ${prefix}${startNum}${suffix}`;
  }

  if (endNum) {
    return `Up to ${prefix}${endNum}${suffix}`;
  }

  return '';
}

export const NumRangeDropdownInput = memo(
  ({
    inputLabel = 'Input Range',
    value = ['', ''],
    inputPlaceholderText = inputLabel,
    updateValue,
    formControlSx,
    popoverPaperSx,
    formControlVariant = 'standard',
    renderValueTooltip,
    renderValueDisplay = 'label',
    infoText = '',
    popoverMatchesFormControlWidth = false,
    applyChangesOnPopoverClose = false,
    disabled = false,
    hidden = false,
    startAdornment,
    valueMode = 'normal',
    keepMounted = false,
    prefix = '',
    suffix = '',
    fieldType = 'tel', // set as number to enable step, tel is mobile keyboard numpad workaround
    decimalPlaces = 0,
    clamp = false,
    step = undefined,
    min = 0,
    max = int32Max,
  }: NumRangeDropdownInputProps) => {
    // UI Vars / Hooks
    const theme = useTheme();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();

    // Min/Max labels on the input fields
    const minLabel = min === 0 ? 'Min' : `Min: ${min}`;
    const maxLabel = max === int32Max ? 'Max' : `Max: ${max}`;

    // Setup consts / state for applyChangesOnPopoverClose prop
    const [stagedValues, setStagedValues] = useState<TwoStrArr>(['', '']);

    // Sync values passed in to internal state
    useEffect(() => {
      const formattedIncomingValues: TwoStrArr = [
        formatForDisplay(value[0], valueMode, decimalPlaces),
        formatForDisplay(value[1], valueMode, decimalPlaces),
      ];

      const currentState: TwoStrArr = [stagedValues[0], stagedValues[1]];

      if (isDeepEqual(currentState, formattedIncomingValues)) {
        return;
      }

      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      // If we pass the guards, update the local state
      setStagedValues(formattedIncomingValues);
    }, [
      stagedValues,
      popoverOpen,
      applyChangesOnPopoverClose,
      value,
      valueMode,
      decimalPlaces,
    ]);

    const currentValues = applyChangesOnPopoverClose ? stagedValues : value;

    const stateInQpFmt: TwoStrArr = [
      formatForQp(currentValues[0], valueMode),
      formatForQp(currentValues[1], valueMode),
    ];

    // Get range label text
    const rangeLabelText = getRangeLabel(
      stateInQpFmt,
      valueMode,
      decimalPlaces,
      prefix,
      suffix,
    );

    // Const helpers
    const isDisabled = disabled;
    const isHidden = hidden;
    const hasStart = currentValues[0] !== '';
    const hasEnd = currentValues[1] !== '';
    const removeButtonEnabled = Boolean(hasStart || hasEnd);

    // Validation logic
    const startIsValid =
      !hasStart || // Empty input is technically valid
      (!isNaN(parseFloat(currentValues[0])) && parseFloat(currentValues[0]) >= min);

    const endIsValid =
      !hasEnd || // Empty input is technically valid
      (!isNaN(parseFloat(currentValues[1])) && parseFloat(currentValues[1]) <= max);

    // Avoid NaN issues and ensure values within range
    const startValue = hasStart ? parseFloat(currentValues[0]) : '';
    const endValue = hasEnd ? parseFloat(currentValues[1]) : '';

    // Range validity empty is valid or values must be in range and correct
    const rangeIsValid =
      !hasStart || !hasEnd || (startIsValid && endIsValid && startValue <= endValue);

    // Either input or range error
    const hasAnyError = !rangeIsValid || !startIsValid || !endIsValid;

    const jsonStringValueOrEmptyStr =
      JSON.stringify(currentValues) === '["",""]' ? '' : JSON.stringify(currentValues);

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip ? rangeLabelText : '';
    const tooltipTitle = !popoverOpen ? tooltipText : '';

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      // Popover close needs QP formatted values to send update
      const adjustedValues: TwoStrArr = [
        formatForQp(stagedValues[0], valueMode),
        formatForQp(stagedValues[1], valueMode),
      ];

      // Clear the invalid values
      if (!startIsValid) {
        adjustedValues[0] = '';
      }

      if (!endIsValid) {
        adjustedValues[1] = '';
      }

      // If range is invalid clear both values
      if (!rangeIsValid) {
        adjustedValues[0] = '';
        adjustedValues[1] = '';
      }

      // Send to external update function
      updateValue(null, adjustedValues);
      setPopoverOpen(false);
    };

    const handleRangeChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      type: 'start' | 'end',
    ) => {
      let newValue = event?.target?.value?.trim();
      const numericLimit = type === 'start' ? min : max;
      const allowNegative = numericLimit < 0;

      // Handle special cases
      if (newValue === '.') {
        // Convert "." to "0."
        newValue = '0.';
      } else if ((newValue === '-' || newValue === '-.') && allowNegative) {
        // Allow negative sign only if negative allowed (fill as -0.)
        newValue = newValue === '-.' ? '-0.' : '-';
      } else {
        // Remove invalid characters except digits, decimal point, and maybe leading negative
        newValue = newValue.replace(/[^0-9.-]/g, '');
        // Negative sign is only at the start if allowed
        if (!allowNegative) {
          newValue = newValue.replace(/-/g, ''); // Remove negatives if not allowed
        } else if (newValue.startsWith('-')) {
          newValue = '-' + newValue.slice(1).replace(/-/g, ''); // Keep only one at the start
        }

        // Only one decimal point is allowed
        const decimalIndex = newValue.indexOf('.');
        if (decimalIndex !== -1) {
          newValue =
            newValue.slice(0, decimalIndex + 1) +
            newValue.slice(decimalIndex + 1).replace(/\./g, '');

          // Truncate after specified decimal places if any existed
          const decimalPart = newValue.slice(decimalIndex + 1);
          if (decimalPart.length > decimalPlaces) {
            newValue = newValue.slice(0, decimalIndex + decimalPlaces + 1);
          }
        }
      }

      // Convert to number and prevent NaN issues mid input
      const parsedValue = parseFloat(newValue);
      if (isNaN(parsedValue) && newValue !== '-' && newValue !== '-0.') {
        newValue = '';
      }

      // Update the range value
      const newRange: TwoStrArr = [...currentValues];
      if (type === 'start') {
        newRange[0] = newValue;
      } else {
        newRange[1] = newValue;
      }

      // Apply changes locally or send to parent
      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(event, newRange);
      }
    };

    const handleBlur = (type: 'start' | 'end') => {
      const newRange: TwoStrArr = [...currentValues];
      let valueToFormat = type === 'start' ? newRange[0] : newRange[1];

      if (valueToFormat) {
        let parsedValue = parseFloat(valueToFormat);
        // On blur ensure input valid (eg not NaN)
        // If NaN, clear the value, else ensure trunc to dec places
        if (isNaN(parsedValue)) {
          valueToFormat = '';
        } else {
          // Apply clamping if prop is true
          if (clamp) {
            if (type === 'start' && parsedValue < min) {
              parsedValue = min; // start cant be less than min
            }
            if (type === 'end' && parsedValue > max) {
              parsedValue = max; // end cant be more than max
            }
          }
          // Convert to string with correct decimal places
          valueToFormat = parsedValue.toFixed(decimalPlaces);
        }
        if (type === 'start') {
          newRange[0] = valueToFormat;
        } else {
          newRange[1] = valueToFormat;
        }

        if (applyChangesOnPopoverClose) {
          setStagedValues(newRange);
        } else {
          updateValue(null, newRange);
        }
      }
    };

    const handleClear = (event: PossibleEvent) => {
      if (applyChangesOnPopoverClose) {
        setStagedValues(['', '']);
      } else {
        updateValue(event, ['', '']);
      }
    };

    const handleClearStart = () => {
      const newRange: TwoStrArr = ['', currentValues[1]];
      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(null, newRange);
      }
    };

    const handleClearEnd = () => {
      const newRange: TwoStrArr = [currentValues[0], ''];
      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(null, newRange);
      }
    };

    return (
      <Tooltip
        title={tooltipTitle}
        disableInteractive={true}
        disableFocusListener={true}
        slotProps={{
          tooltip: {
            sx: {
              ...getTooltipSx(theme),
            },
          },
        }}
      >
        <FormControl
          size="small"
          variant={formControlVariant}
          ref={elSizeRef}
          disabled={isDisabled}
          hidden={isHidden}
          sx={{
            ...getFormControlSx(theme, popoverOpen),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
            ...(isHidden ? { display: 'none' } : {}),
          }}
        >
          <Select
            ref={popoverAnchorRef}
            multiple={false}
            disabled={isDisabled}
            hidden={isHidden}
            id={`${inputLabel}-range-select-input`}
            value={jsonStringValueOrEmptyStr}
            MenuProps={{ open: false }}
            onOpen={handlePopoverOpen}
            open={popoverOpen}
            sx={getSelectSx}
            displayEmpty={true}
            renderValue={() => (
              <SelectRenderValueElem
                selectedChoices={
                  jsonStringValueOrEmptyStr
                    ? [{ value: jsonStringValueOrEmptyStr, label: rangeLabelText }]
                    : []
                }
                placeholderText={inputPlaceholderText || inputLabel}
                renderValueDisplay={renderValueDisplay}
                isError={hasAnyError}
              />
            )}
          >
            <MenuItem
              value=""
              hidden={true}
              style={{ display: 'none' }}
            />
            <MenuItem
              value={jsonStringValueOrEmptyStr}
              hidden={true}
              style={{ display: 'none' }}
            />
          </Select>

          <Popover
            keepMounted={keepMounted}
            anchorEl={popoverAnchorRef?.current}
            open={popoverOpen && !isDisabled}
            hidden={isHidden}
            onClose={handlePopoverClose}
            anchorOrigin={dropdownInputAnchorOrigin}
            transformOrigin={dropdownInputTransformOrigin}
            elevation={dropdownInputElevation}
            marginThreshold={getDropdownInputThreshold(theme)}
            slotProps={{
              paper: {
                sx: {
                  display: 'grid',
                  gridTemplateRows: 'auto auto 1fr',
                  gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                  container: 'selectgrid / normal',
                  overflow: 'hidden',
                  width: popoverMatchesFormControlWidth
                    ? formCtrlWidth
                    : maxPopoverWidthPxRange,
                  maxHeight: maxPopoverHeightVh,
                  gap: 0.5,
                  bgcolor: getSolidThemeGray(theme),
                  ...getCommonPopoutSx(theme),
                  ...(typeof popoverPaperSx === 'function'
                    ? popoverPaperSx(theme)
                    : popoverPaperSx),
                },
              },
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gridArea: 'selectheader',
                p: 1,
                borderBottom: `1px solid ${theme.palette.action.selected}`,
              }}
            >
              <Tooltip
                title={infoText}
                arrow={false}
                disableInteractive={true}
                enterDelay={500}
                placement="bottom-start"
                slotProps={{
                  popper: tooltipPopperProps,
                }}
              >
                <Typography
                  sx={getHeaderLabelTypoSx}
                  aria-label={`${inputLabel} - ${infoText}`}
                >
                  {inputLabel}
                  {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                </Typography>
              </Tooltip>
              <Button
                variant="text"
                size="small"
                color="error"
                disabled={!removeButtonEnabled}
                startIcon={<RemoveCircleOutlineIcon />}
                sx={getRemoveButtonSx}
                onClick={handleClear}
              >
                Remove
              </Button>
            </Stack>

            <Stack
              direction="column"
              spacing={1}
              sx={{ gridArea: 'selectmainarea', px: 1 }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <TextField
                  label={minLabel} // show min value if provided
                  type={fieldType} // tel mobile keyboard numpad workaround
                  autoComplete="off"
                  disabled={isDisabled}
                  onChange={(event) => handleRangeChange(event, 'start')}
                  onBlur={() => handleBlur('start')}
                  value={currentValues[0]}
                  size="small"
                  variant="outlined"
                  error={!rangeIsValid || !startIsValid}
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    '& .MuiOutlinedInput-root': {
                      px: '4px',
                      py: '0px',
                    },
                    // Target the start adornment svg but not the end adornment
                    '& .MuiInputBase-root > svg:not(.MuiInputAdornment-positionEnd svg)':
                      {
                        color: theme.palette.text.disabled,
                        fontSize: 24,
                      },
                  }}
                  slotProps={{
                    input: {
                      inputProps: {
                        'data-testid': 'min-input',
                        step: step,
                      },
                      startAdornment: startAdornment || null,
                      endAdornment: (
                        <Fade in={hasStart}>
                          <InputAdornment position="end">
                            <Tooltip
                              title="Clear"
                              disableInteractive
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <ClearIcon
                                fontSize="small"
                                onClick={(event) => handleClearStart()}
                              />
                            </Tooltip>
                          </InputAdornment>
                        </Fade>
                      ),
                    },
                  }}
                />
                <Typography sx={{ fontSize: bloxValues.smallFontSizeRem }}>
                  {emdash}
                </Typography>
                <TextField
                  label={maxLabel} // show max value if provided
                  type={fieldType} // tel mobile keyboard numpad workaround
                  autoComplete="off"
                  disabled={isDisabled}
                  onChange={(event) => handleRangeChange(event, 'end')}
                  onBlur={() => handleBlur('end')}
                  value={currentValues[1]}
                  size="small"
                  variant="outlined"
                  error={!rangeIsValid || !endIsValid}
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    '& .MuiOutlinedInput-root': {
                      px: '4px',
                      py: '0px',
                    },
                    '& .MuiInputBase-root > svg:not(.MuiInputAdornment-positionEnd svg)':
                      {
                        color: theme.palette.text.disabled,
                        fontSize: 24,
                      },
                  }}
                  slotProps={{
                    input: {
                      inputProps: {
                        'data-testid': 'max-input',
                        step: step,
                      },
                      startAdornment: startAdornment || null,
                      endAdornment: (
                        <Fade in={hasEnd}>
                          <InputAdornment
                            position="end"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <Tooltip
                              title="Clear"
                              disableInteractive
                            >
                              <ClearIcon
                                fontSize="small"
                                onClick={(event) => handleClearEnd()}
                              />
                            </Tooltip>
                          </InputAdornment>
                        </Fade>
                      ),
                    },
                  }}
                />
              </Stack>
              {!rangeIsValid ? (
                <Typography
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: bloxValues.smallFontSizeRem,
                  }}
                >
                  The range is invalid. Please check the values.
                </Typography>
              ) : null}
            </Stack>

            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gridTemplateAreas: `"listinfo closebtn"`,
                alignItems: 'center',
                ...getFooterContainerSx(theme, true),
              }}
            >
              {/* Spacer to keep the close button location consistent with other advanced filters */}
              <Box sx={{ gridArea: 'listinfo' }} />

              <ButtonBase
                onClick={handlePopoverClose}
                sx={{
                  gridArea: 'closebtn',
                  ...getCloseButtonSx(theme),
                }}
              >
                Close
              </ButtonBase>
            </Stack>
          </Popover>
        </FormControl>
      </Tooltip>
    );
  },
);
NumRangeDropdownInput.displayName = 'NumRangeDropdownInput';
